"use client";

import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import React from "react";

import { useViewport } from "../hooks/useViewport";

import { IProps as IImportedProps } from "./index.d";
import { ResponsiveContentV2 as StaticResponsiveContent } from "./index.rsc";

export type IProps = IImportedProps;

/**
 * This component can be used to conditionally display content based on the viewport.
 *
 * If used with the "noJS" prop, if used as a React Server Component (RSC),
 * or during SSR, it will render all possible viewports,
 * hiding ones that do not apply using media queries.
 *
 * It is not valid to pass noJS={false} if rendered from a React Server Component (RSC).
 */
export const ResponsiveContentV2: React.FC<IProps> =
  // eslint-disable-next-line complexity
  (props) => {
    const {
      mobile,
      tablet,
      desktop,
      xlarge,
      xxlarge,
      tabletAndAbove,
      tabletAndBelow,
      desktopAndAbove,
      xLargeAndAbove,
      noJS,
    } = props;
    const isSSR = useIsSSR();
    const viewport = useViewport();

    if (isSSR || noJS) {
      return <StaticResponsiveContent {...props} noJS={true} />;
    }
    if (viewport.isMobile && mobile) {
      return <>{mobile}</>;
    }
    if (viewport.isTablet && tablet) {
      return <>{tablet}</>;
    }
    if (viewport.isDesktop && desktop) {
      return <>{desktop}</>;
    }
    if (viewport.isXLargeDesktop && xlarge) {
      return <>{xlarge}</>;
    }
    if (viewport.isXXLargeDesktop && xxlarge) {
      return <>{xxlarge}</>;
    }
    if (viewport.isXLargeAndAbove && xLargeAndAbove) {
      return <>{xLargeAndAbove}</>;
    }
    if (viewport.isDesktopAndAbove && desktopAndAbove) {
      return <>{desktopAndAbove}</>;
    }
    if (viewport.isTabletAndAbove && tabletAndAbove) {
      return <>{tabletAndAbove}</>;
    }
    if (viewport.isTabletAndBelow && tabletAndBelow) {
      return <>{tabletAndBelow}</>;
    }
    return null;
  };
